import { useEffect, useState } from 'react'
import { Globe, Link2, Settings2, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { useConfirm } from '@/components/ui/confirm-provider'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { LinkToGlobalDialog } from '@/components/catalog/link-to-global-dialog'
import { LinkedToGlobalChip } from '@/components/catalog/linked-to-global-chip'
import { ManageSourcesDialog } from '@/components/catalog/manage-sources-dialog'
import { SourceAttributionBadge } from '@/components/catalog/source-attribution-badge'
import {
  useDeleteMaterial,
  useDeleteTool,
  useLinkProjectMaterialToGlobal,
  useLinkProjectToolToGlobal,
  useUpdateMaterial,
  useUpdateTool,
} from '@/features/projects/use-catalog'
import type { ApiError, CatalogMaterial, CatalogTool, ProjectCatalog } from '@/api/types'

interface CatalogDrawerProps {
  projectId: string
  catalog: ProjectCatalog
}

const dollarsToCents = (input: string): number | null => {
  const trimmed = input.trim()
  if (!trimmed) return null
  const num = Number(trimmed)
  if (!Number.isFinite(num) || num < 0) return null
  return Math.round(num * 100)
}

const apiErrorMessage = (err: unknown, fallback: string): string =>
  (err as ApiError | undefined)?.detail ?? fallback

interface MaterialRowProps {
  projectId: string
  material: CatalogMaterial
}

const MaterialRow = ({ projectId, material }: MaterialRowProps) => {
  const update = useUpdateMaterial(projectId)
  const remove = useDeleteMaterial(projectId)
  const link = useLinkProjectMaterialToGlobal(projectId)
  const confirm = useConfirm()
  const [name, setName] = useState(material.name)
  const [unit, setUnit] = useState(material.unit)
  const [price, setPrice] = useState((material.unitPriceCents / 100).toFixed(2))
  const [linkOpen, setLinkOpen] = useState(false)
  const [sourcesOpen, setSourcesOpen] = useState(false)
  const isBound = (material.globalCatalogId ?? null) != null
  const liveSource = material.liveSource ?? null
  const liveTier: 'live' | 'default' =
    liveSource?.lastFetchedPriceCents != null ? 'live' : 'default'

  const handleBind = (globalId: string) => {
    link.mutate(
      { materialId: material.id, request: { globalCatalogMaterialId: globalId } },
      {
        onSuccess: () => {
          setLinkOpen(false)
          toast.success(`Linked "${material.name}" to shared catalog`)
        },
        onError: (e) => toast.error(apiErrorMessage(e, 'Failed to link to shared catalog')),
      }
    )
  }

  useEffect(() => {
    setName(material.name)
    setUnit(material.unit)
    setPrice((material.unitPriceCents / 100).toFixed(2))
  }, [material.name, material.unit, material.unitPriceCents])

  const saveName = () => {
    const next = name.trim()
    if (!next) {
      setName(material.name)
      return
    }
    if (next === material.name) return
    update.mutate(
      { id: material.id, data: { name: next } },
      { onError: (e) => toast.error(apiErrorMessage(e, 'Failed to update name')) }
    )
  }

  const saveUnit = () => {
    const next = unit.trim()
    if (!next) {
      setUnit(material.unit)
      return
    }
    if (next === material.unit) return
    update.mutate(
      { id: material.id, data: { unit: next } },
      { onError: (e) => toast.error(apiErrorMessage(e, 'Failed to update unit')) }
    )
  }

  const savePrice = () => {
    const cents = dollarsToCents(price)
    if (cents === null) {
      setPrice((material.unitPriceCents / 100).toFixed(2))
      toast.error('Price must be a non-negative number')
      return
    }
    if (cents === material.unitPriceCents) return
    update.mutate(
      { id: material.id, data: { unitPriceCents: cents } },
      { onError: (e) => toast.error(apiErrorMessage(e, 'Failed to update price')) }
    )
  }

  const handleDelete = async () => {
    const ok = await confirm({
      title: `Delete "${material.name}"?`,
      confirmText: 'Delete',
      destructive: true,
    })
    if (!ok) return
    remove.mutate(material.id, {
      onError: (e) => toast.error(apiErrorMessage(e, 'Failed to delete material')),
    })
  }

  return (
    <li className="space-y-1 py-1.5">
      <div className="grid grid-cols-[1fr_4rem_5rem_2rem_2rem] items-center gap-2">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={saveName}
          className="h-8 text-sm"
          aria-label={`${material.name} name`}
        />
        <Input
          value={unit}
          onChange={(e) => setUnit(e.target.value)}
          onBlur={saveUnit}
          className="h-8 text-sm"
          aria-label={`${material.name} unit`}
        />
        <Input
          type="number"
          step="0.01"
          min="0"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          onBlur={savePrice}
          className="h-8 text-sm tabular-nums"
          aria-label={`${material.name} price`}
        />
        {!isBound && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
            onClick={() => setLinkOpen(true)}
            aria-label={`Link ${material.name} to shared catalog`}
            title="Link to shared catalog"
          >
            <Globe className="h-3 w-3" />
          </Button>
        )}
        {isBound && <span aria-hidden />}
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-destructive"
          onClick={handleDelete}
          disabled={remove.isPending}
          aria-label={`Delete ${material.name}`}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
      {isBound && (
        <div className="ml-1 flex flex-wrap items-center gap-2">
          <LinkedToGlobalChip usageCount={material.linkedGlobalUsageCount ?? null} />
          {liveTier === 'live' && (
            <SourceAttributionBadge
              tier="live"
              liveSource={liveSource}
              isStale={liveSource?.isStale ?? false}
            />
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-6 gap-1 px-2 text-[11px]"
            onClick={() => setSourcesOpen(true)}
            aria-label={`Manage sources for ${material.name}`}
          >
            <Link2 className="h-3 w-3" />
            Sources
          </Button>
        </div>
      )}
      {linkOpen && (
        <LinkToGlobalDialog
          open={linkOpen}
          onOpenChange={setLinkOpen}
          kind="MATERIAL"
          seedName={material.name}
          seedUnit={material.unit}
          pending={link.isPending}
          onPickExisting={(id) => handleBind(id)}
          onPickNewlyCreated={(id) => handleBind(id)}
        />
      )}
      {sourcesOpen && material.globalCatalogId && (
        <ManageSourcesDialog
          open={sourcesOpen}
          onOpenChange={setSourcesOpen}
          kind="MATERIAL"
          globalCatalogId={material.globalCatalogId}
          globalCatalogName={material.name}
        />
      )}
    </li>
  )
}

interface ToolRowProps {
  projectId: string
  tool: CatalogTool
}

const ToolRow = ({ projectId, tool }: ToolRowProps) => {
  const update = useUpdateTool(projectId)
  const remove = useDeleteTool(projectId)
  const link = useLinkProjectToolToGlobal(projectId)
  const confirm = useConfirm()
  const [name, setName] = useState(tool.name)
  const [price, setPrice] = useState((tool.unitPriceCents / 100).toFixed(2))
  const [linkOpen, setLinkOpen] = useState(false)
  const [sourcesOpen, setSourcesOpen] = useState(false)
  const isBound = (tool.globalCatalogId ?? null) != null
  const liveSource = tool.liveSource ?? null
  const liveTier: 'live' | 'default' =
    liveSource?.lastFetchedPriceCents != null ? 'live' : 'default'

  const handleBind = (globalId: string) => {
    link.mutate(
      { toolId: tool.id, request: { globalCatalogToolId: globalId } },
      {
        onSuccess: () => {
          setLinkOpen(false)
          toast.success(`Linked "${tool.name}" to shared catalog`)
        },
        onError: (e) => toast.error(apiErrorMessage(e, 'Failed to link to shared catalog')),
      }
    )
  }

  useEffect(() => {
    setName(tool.name)
    setPrice((tool.unitPriceCents / 100).toFixed(2))
  }, [tool.name, tool.unitPriceCents])

  const saveName = () => {
    const next = name.trim()
    if (!next) {
      setName(tool.name)
      return
    }
    if (next === tool.name) return
    update.mutate(
      { id: tool.id, data: { name: next } },
      { onError: (e) => toast.error(apiErrorMessage(e, 'Failed to update name')) }
    )
  }

  const savePrice = () => {
    const cents = dollarsToCents(price)
    if (cents === null) {
      setPrice((tool.unitPriceCents / 100).toFixed(2))
      toast.error('Price must be a non-negative number')
      return
    }
    if (cents === tool.unitPriceCents) return
    update.mutate(
      { id: tool.id, data: { unitPriceCents: cents } },
      { onError: (e) => toast.error(apiErrorMessage(e, 'Failed to update price')) }
    )
  }

  const handleDelete = async () => {
    const ok = await confirm({
      title: `Delete "${tool.name}"?`,
      confirmText: 'Delete',
      destructive: true,
    })
    if (!ok) return
    remove.mutate(tool.id, {
      onError: (e) => toast.error(apiErrorMessage(e, 'Failed to delete tool')),
    })
  }

  return (
    <li className="space-y-1 py-1.5">
      <div className="grid grid-cols-[1fr_5rem_2rem_2rem] items-center gap-2">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={saveName}
          className="h-8 text-sm"
          aria-label={`${tool.name} name`}
        />
        <Input
          type="number"
          step="0.01"
          min="0"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          onBlur={savePrice}
          className="h-8 text-sm tabular-nums"
          aria-label={`${tool.name} price`}
        />
        {!isBound && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
            onClick={() => setLinkOpen(true)}
            aria-label={`Link ${tool.name} to shared catalog`}
            title="Link to shared catalog"
          >
            <Globe className="h-3 w-3" />
          </Button>
        )}
        {isBound && <span aria-hidden />}
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-destructive"
          onClick={handleDelete}
          disabled={remove.isPending}
          aria-label={`Delete ${tool.name}`}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
      {isBound && (
        <div className="ml-1 flex flex-wrap items-center gap-2">
          <LinkedToGlobalChip usageCount={tool.linkedGlobalUsageCount ?? null} />
          {liveTier === 'live' && (
            <SourceAttributionBadge
              tier="live"
              liveSource={liveSource}
              isStale={liveSource?.isStale ?? false}
            />
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-6 gap-1 px-2 text-[11px]"
            onClick={() => setSourcesOpen(true)}
            aria-label={`Manage sources for ${tool.name}`}
          >
            <Link2 className="h-3 w-3" />
            Sources
          </Button>
        </div>
      )}
      {linkOpen && (
        <LinkToGlobalDialog
          open={linkOpen}
          onOpenChange={setLinkOpen}
          kind="TOOL"
          seedName={tool.name}
          seedUnit=""
          pending={link.isPending}
          onPickExisting={(id) => handleBind(id)}
          onPickNewlyCreated={(id) => handleBind(id)}
        />
      )}
      {sourcesOpen && tool.globalCatalogId && (
        <ManageSourcesDialog
          open={sourcesOpen}
          onOpenChange={setSourcesOpen}
          kind="TOOL"
          globalCatalogId={tool.globalCatalogId}
          globalCatalogName={tool.name}
        />
      )}
    </li>
  )
}

export const CatalogDrawer = ({ projectId, catalog }: CatalogDrawerProps) => (
  <Sheet>
    <SheetTrigger asChild>
      <Button variant="outline" size="sm" className="gap-2">
        <Settings2 className="h-4 w-4" />
        Catalog
      </Button>
    </SheetTrigger>
    <SheetContent side="right" className="w-full sm:max-w-md">
      <SheetHeader>
        <SheetTitle>Project catalog</SheetTitle>
        <SheetDescription>
          Manage every material and tool defined for this project. Items in use can&apos;t be
          deleted until they&apos;re detached from steps.
        </SheetDescription>
      </SheetHeader>

      <div className="mt-6 space-y-6">
        <section>
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Materials
          </h3>
          {catalog.materials.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">
              No materials yet. Add one from a step using the &ldquo;+ Material&rdquo; picker.
            </p>
          ) : (
            <>
              <div className="mt-2 grid grid-cols-[1fr_4rem_5rem_2rem] gap-2 px-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                <span>Name</span>
                <span>Unit</span>
                <span>Price</span>
                <span aria-hidden />
              </div>
              <ul className="divide-y">
                {catalog.materials.map((m) => (
                  <MaterialRow key={m.id} projectId={projectId} material={m} />
                ))}
              </ul>
            </>
          )}
        </section>

        <section>
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Tools
          </h3>
          {catalog.tools.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">
              No tools yet. Add one from a step using the &ldquo;+ Tool&rdquo; picker.
            </p>
          ) : (
            <>
              <div className="mt-2 grid grid-cols-[1fr_5rem_2rem] gap-2 px-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                <span>Name</span>
                <span>Price</span>
                <span aria-hidden />
              </div>
              <ul className="divide-y">
                {catalog.tools.map((t) => (
                  <ToolRow key={t.id} projectId={projectId} tool={t} />
                ))}
              </ul>
            </>
          )}
        </section>
      </div>
    </SheetContent>
  </Sheet>
)
